import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { base_url } from "constant/constant";

interface WishlistState {
  savedIds: string[];
  isSaved: (propertyId?: string) => boolean;
  toggleWishlist: (propertyId?: string) => Promise<void>;
  refreshWishlist: () => Promise<void>;
  mergeSaved: (ids: string[]) => void;
}

const WishlistContext = createContext<WishlistState | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [savedIds, setSavedIds] = useState<string[]>([]);

  const refreshWishlist = useCallback(async () => {
    try {
      const res = await axios.get(`${base_url}/wishlist/wishlist_ids`);
      if (!res.data?.error && Array.isArray(res.data?.data)) {
        setSavedIds(res.data.data.map((id: any) => String(id)));
      }
    } catch (error) {
      // Wishlist is a non-critical enhancement; fail quietly.
    }
  }, []);

  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  // Seed the saved set from an authoritative source (e.g. list_wishlist) so the
  // heart state stays consistent even if the ids fetch was slow or failed.
  const mergeSaved = useCallback((ids: string[]) => {
    setSavedIds((prev) => Array.from(new Set([...prev, ...ids.map(String)])));
  }, []);

  const isSaved = useCallback(
    (propertyId?: string) =>
      !!propertyId && savedIds.includes(String(propertyId)),
    [savedIds]
  );

  const toggleWishlist = useCallback(
    async (propertyId?: string) => {
      if (!propertyId) return;
      const id = String(propertyId);
      const wasSaved = savedIds.includes(id);

      // Optimistic update so the heart reacts instantly.
      setSavedIds((prev) =>
        wasSaved ? prev.filter((x) => x !== id) : [...prev, id]
      );

      try {
        const res = await axios.post(`${base_url}/wishlist/toggle_wishlist`, {
          property_id: id,
        });
        if (res.data?.error) {
          throw new Error(res.data?.title || "Something went wrong");
        }
        const liked = res.data?.liked;
        // Reconcile with the server's authoritative state.
        setSavedIds((prev) => {
          const without = prev.filter((x) => x !== id);
          return liked ? [...without, id] : without;
        });
        toast.success(
          liked ? "Added to wishlist" : "Removed from wishlist"
        );
      } catch (error: any) {
        // Roll back the optimistic update.
        setSavedIds((prev) => {
          const without = prev.filter((x) => x !== id);
          return wasSaved ? [...without, id] : without;
        });
        toast.error(error?.message || "Could not update wishlist");
      }
    },
    [savedIds]
  );

  return (
    <WishlistContext.Provider
      value={{ savedIds, isSaved, toggleWishlist, refreshWishlist, mergeSaved }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
